import { useRef, useLayoutEffect } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const steps = [
  {
    number: '01',
    title: 'Index with Oak',
    body: 'Integrate your products. They get smarter over time — enriched with real context that travels wherever they go.',
  },
  {
    number: '02',
    title: 'Distribute everywhere',
    body: 'Reach your community wherever it lives — on Nanza or third party platforms. Oak keeps every surface in sync.',
  },
  {
    number: '03',
    title: 'Track and grow',
    body: 'Oak tracks the signals that matter to you and feeds them back. See what resonates, find new partners, and grow your community.',
  },
]

/* ── Card final positions (hand-placed feel) ─────── */
const cardFinals = [
  { x: 18, y: 0, rotation: -2.5 },
  { x: -8, y: 65, rotation: 2.5 },
  { x: 12, y: 130, rotation: -2 },
]

const cardInitials = [
  { x: 8, y: 120, rotation: -4, opacity: 0, scale: 0.985 },
  { x: -18, y: 300, rotation: 4, opacity: 0, scale: 0.99 },
  { x: 14, y: 350, rotation: -4, opacity: 0, scale: 0.99 },
]

/* ── Mobile: centered, no tilt, same vertical stacking ─── */
const cardFinalsMobile = [
  { x: 0, y: 0, rotation: 0 },
  { x: 0, y: 65, rotation: 0 },
  { x: 0, y: 130, rotation: 0 },
]

const cardInitialsMobile = [
  { x: 0, y: 120, rotation: 0, opacity: 0, scale: 0.985 },
  { x: 0, y: 300, rotation: 0, opacity: 0, scale: 0.99 },
  { x: 0, y: 350, rotation: 0, opacity: 0, scale: 0.99 },
]

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])

  useLayoutEffect(() => {
    const section = sectionRef.current
    const stage = stageRef.current
    const cards = cardRefs.current
    if (!section || !stage || cards.some(c => !c)) return

    const buildTimeline = (
      initials: typeof cardInitials,
      finals: typeof cardFinals,
      scrollMultiplier: number,
      endHold = 0,
    ) => {
      cards.forEach((card, i) => {
        gsap.set(card, {
          x: initials[i].x,
          y: initials[i].y,
          rotation: initials[i].rotation,
          opacity: initials[i].opacity,
          scale: initials[i].scale,
          willChange: 'transform, opacity',
        })
      })

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          // Pin distance is computed in JS pixels (not CSS vh) so the scroll
          // length stays in lockstep with the timeline across browsers whose
          // vh / innerHeight semantics diverge (e.g. DuckDuckGo).
          end: () => '+=' + window.innerHeight * scrollMultiplier,
          pin: stage,
          pinSpacing: true,
          scrub: 0.8,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      })

      tl.to(cards[0], { x: finals[0].x, y: finals[0].y, rotation: finals[0].rotation, opacity: 1, scale: 1, ease: 'power3.out', duration: 0.20 }, 0)
      tl.to(cards[1], { x: finals[1].x, y: finals[1].y, rotation: finals[1].rotation, opacity: 1, scale: 1, ease: 'power3.out', duration: 0.25 }, 0.20)
      tl.to(cards[2], { x: finals[2].x, y: finals[2].y, rotation: finals[2].rotation, opacity: 1, scale: 1, ease: 'power3.out', duration: 0.25 }, 0.45)

      // Hold cards in place at the end so the pin releases later — gives a buffer flick before the next section.
      if (endHold > 0) tl.to({}, { duration: endHold }, 0.70)

      return tl
    }

    const mm = gsap.matchMedia()

    mm.add('(min-width: 768px)', () => {
      const tl = buildTimeline(cardInitials, cardFinals, 1.5)
      return () => tl.kill()
    })

    mm.add('(max-width: 767px)', () => {
      const tl = buildTimeline(cardInitialsMobile, cardFinalsMobile, 2.1, 0.30)
      return () => tl.kill()
    })

    const refresh = () => ScrollTrigger.refresh()
    const fontsReady = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts?.ready
    if (fontsReady) fontsReady.then(refresh)
    window.addEventListener('load', refresh)

    return () => {
      window.removeEventListener('load', refresh)
      mm.revert()
    }
  }, [])

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      className="relative bg-oak-cream scroll-mt-24"
    >
      <div
        ref={stageRef}
        className="flex min-h-screen items-start px-6 pt-[18vh] md:px-16"
      >
        <div className="mx-auto grid w-full max-w-nav grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-16">
          {/* ── Left: static content ─────────── */}
          <div className="max-w-[440px]">
            <h2 className="font-serif text-[14vw] leading-[0.95] tracking-[-1.5px] text-oak-black md:text-[clamp(40px,6vw,80px)]">
              How Oak works
            </h2>
            <p className="mt-6 max-w-[460px] text-[clamp(16px,1.8vw,22px)] font-medium leading-[1.3] tracking-[-0.2px] text-oak-black/70 md:mt-8">
              Oak is a partner at every step. We enrich your products, keep them consistent across every platform, and bring the signals back to you.
            </p>
          </div>

          {/* ── Right: card stage ─────────────── */}
          <div className="relative" style={{ height: '420px' }}>
            {steps.map((step, i) => (
              <div
                key={step.number}
                ref={el => { cardRefs.current[i] = el }}
                className="absolute top-0 left-0 right-0 rounded-3xl bg-white p-8 md:p-10"
                style={{
                  zIndex: i + 1,
                  boxShadow: `0 ${4 + i * 4}px ${12 + i * 8}px rgba(0,0,0,${0.04 + i * 0.02}), 0 ${1 + i}px ${3 + i * 2}px rgba(0,0,0,${0.03 + i * 0.01})`,
                  transformOrigin: 'center center',
                }}
              >
                <span className="text-sm font-semibold uppercase tracking-widest text-gold-500">
                  Step {step.number}
                </span>
                <h3 className="mt-3 text-[clamp(24px,3vw,32px)] font-semibold leading-tight tracking-[-0.5px] text-oak-black">
                  {step.title}
                </h3>
                <p className="mt-4 text-[clamp(15px,1.4vw,18px)] leading-[1.5] tracking-[-0.2px] text-oak-black/70">
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
